import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Lock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function RoleDialog({ role, perms, onClose, onSaved }) {
  const { meta } = useAuth();
  const [f, setF] = useState({ name: "", permissions: [], markets: [], active: true });
  useEffect(() => { if (role) setF(role.id ? { name: role.name, permissions: role.permissions, markets: role.markets || [], active: role.active } : { name: "", permissions: [], markets: [], active: true }); }, [role]);
  const toggle = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });
  const save = async () => {
    try {
      role.id ? await api.patch(`/roles/${role.id}`, f) : await api.post("/roles", f);
      toast.success("Role saved");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!role} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
        <DialogHeader><DialogTitle>{role?.id ? `Edit role: ${role.name}` : "New role"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Role name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="role-name-input" /></div>
          <div>
            <Label>Permissions</Label>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {perms.map((p) => (
                <label key={p.key} className="flex items-start gap-2 text-sm"><Checkbox checked={f.permissions.includes(p.key)} onCheckedChange={() => toggle("permissions", p.key)} data-testid={`perm-${p.key}`} /><span>{p.label}<span className="block text-[10px] text-slate-400 num">{p.key}</span></span></label>
              ))}
            </div>
          </div>
          <div>
            <Label>Market access (none selected = all markets)</Label>
            <div className="flex flex-wrap gap-4 mt-2">
              {meta.markets.map((m) => <label key={m.key} className="flex items-center gap-2 text-sm"><Checkbox checked={f.markets.includes(m.key)} onCheckedChange={() => toggle("markets", m.key)} data-testid={`role-market-${m.key}`} />{m.name}</label>)}
            </div>
          </div>
          <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="role-active-switch" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="role-save-button">Save role</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get("/roles"), api.get("/permissions")]);
    setRoles(r.data);
    setPerms(p.data);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);
  return (
    <div>
      <div className="flex justify-end mb-3"><Button onClick={() => setEdit({})} className="bg-orange-500 hover:bg-orange-600" data-testid="add-role-button"><Plus className="h-4 w-4 mr-1" />New role</Button></div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {roles.map((r) => (
          <div key={r.id} className={`bg-white border rounded-md p-4 ${r.active ? "" : "opacity-60"}`} data-testid={`role-card-${r.key}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-semibold">{r.name}</div><div className="text-xs text-slate-500">{r.user_count} active user(s){!r.active && " · inactive"}{r.markets?.length ? ` · ${r.markets.join(", ")}` : " · all markets"}</div></div>
              {r.system ? <Lock className="h-4 w-4 text-slate-400" /> : <Button size="sm" variant="outline" onClick={() => setEdit(r)} data-testid={`edit-role-${r.key}`}><Pencil className="h-3.5 w-3.5" /></Button>}
            </div>
            <div className="flex flex-wrap gap-1 mt-3">
              {r.permissions.includes("*") ? <span className="text-[10px] bg-[#0B192C] text-white rounded px-1.5 py-0.5">ALL PERMISSIONS</span> :
                r.permissions.map((p) => <span key={p} className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5 num">{p}</span>)}
            </div>
          </div>
        ))}
      </div>
      <RoleDialog role={edit} perms={perms} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
